<template>
  <div
    ref="refShowdownViewer"
    v-html="transformedMsg"
    class="markdown-viewer"
    @click="onMarkdownClick"
  ></div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  useSlots,
} from "vue";

import Showdown from "showdown";
// import showdownKatex from "showdown-katex";
// import katex from "katex";
// import "katex/dist/katex.min.css";
// katex.renderToString("P(x) = x^3 - 5x^2 + 6x");
import tools from "@/utils/tools.js";

// 定义名为 'cursor' 的扩展
Showdown.extension("cursor", function () {
  return [
    {
      type: "lang", // 指定扩展的类型为 "lang"（处理 Markdown 内容）
      regex: /\[\[\[cursor\]\]\]/g, // 匹配 [cursor] 标记
      replace: '<span class="cursor-dot"></span>', // 替换为 HTML
    },
  ];
});

const $slots = useSlots();

const refShowdownViewer = ref(null);

const props = defineProps({
  markdownText: {
    type: String,
    default: () => {
      return "";
    },
  },
  isWriting: {
    type: Boolean,
    default: () => {
      return false;
    },
  },
});

const transformedMsg = ref("");

const formatFormulaNoBr2 = (strVal) => {
  // getFormulaRegList中，去掉reg4, reg5（解决行间样式不居中，行内样式换行问题）
  // 去掉reg3, reg33, reg7（解决将公式内部语法中的 '\{'、'\}' 识别成 $ 问题）
  const reg1 = /\\\[\s(.*?)\s\\\]/g;
  const reg11 = /\\\[(.*?)\\\]/g;
  const reg2 = /\\\(\s(.*?)\s\\\)/g;
  const reg22 = /\\\((.*?)\\\)/g;
  const reg6 = /\\\[([\s\S]*?)\\\]/g;
  const reg8 = /\\\(([\s\S]*?)\\\)/g;
  const regArr = [reg1, reg11, reg2, reg22, reg6, reg8];
  for (const i in regArr) {
    strVal = strVal.replace(regArr[i], (_, p1) => {
      if (p1.indexOf("begin{align*}") != -1 || i == "4" || i == "5") {
        // 4, 5的情况是换行的公式，需要双$包裹
        return `$$${p1}$$`;
      } else {
        // 去掉换行，解决行内公式出现换行问题
        return `$${p1}$`;
      }
    });
  }
  return strVal;
};
/**
 *
 * @param strVal 用来处理katex解析问题
 * @returns
 */
const replaceBigBrackets = (strVal) => {
  // 定义需要替换的模式和对应的替换值
  const pattern = /[Bb]ig{([[\]{}|()])}/g; // big 括号问题
  const underLinePattern = /\^_/g; // 下划线问题

  // 使用替换函数来处理匹配的内容
  let text = strVal.replace(pattern, (match, p1) => {
    return `Big${p1}`;
  });
  return text.replace(underLinePattern, "^");
};

function transformMsg(msgInfo = "") {
  msgInfo = replaceBigBrackets(formatFormulaNoBr2(msgInfo))
    .replaceAll("<br />", "\n")
    .replace(/\\+n/g, "\n")
    .replace(/[]/g, "\r")
    .replace(/\\+t/g, "\t"); // 一些处理

  let converter = new Showdown.Converter({
    // 详细配置见：https://github.com/showdownjs/showdown#valid-options
    tables: true, // 支持table语法
    strikethrough: true, // 将~~strikethrough~~ 识别为 <del>strikethrough</del>
    underline: true, // __test__中的下划线不识别为<em> 和 <strong>
    simpleLineBreaks: true,
    extensions: [
      "cursor",
      // showdownKatex({
      //   // 使用方式见：https://obedm503.github.io/showdown-katex
      //   // 详细配置见：https://katex.org/docs/options.html
      //   // maybe you want katex to throwOnError
      //   throwOnError: true,
      //   displayMode: true,
      //   errorColor: "#1500ff",
      //   delimiters: [
      //     { left: "$$", right: "$$", display: false },
      //     { left: "$", right: "$", display: false },
      //     // { left: "\(", right: "\)", display: false },
      //     // { left: "\[", right: "\]", display: false },

      //     // { left: "$$", right: "$$", display: false },
      //     // { left: "[", right: "]", display: false },
      //     // { left: "$", right: "$", display: false },
      //     // { left: "(", right: ")", display: false },
      //     // { left: "~", right: "~", display: false, asciimath: true },
      //   ],
      // }),
    ],
  });
  // converter.setFlavor("github"); // github风格
  if (props.isWriting) {
    // 如果正在输入，添加光标
    return converter.makeHtml(
      msgInfo + (props.isWriting ? "[[[cursor]]]" : "")
    );
  }
  return converter.makeHtml(msgInfo);
  // msgInfo = msgInfo.replace(/<br \/>/g, "<br />[[[cursor]]]");
}

const renderDownlatex = () => {
  const msgContent = props.markdownText;
  const transformedHtml = transformMsg(msgContent);
  transformedMsg.value = transformedHtml;
};

const initCopyEvent = () => {
  refShowdownViewer.value?.addEventListener("click", (e) => {
    if (e.target.tagName.toLowerCase() === "code") {
      const code = e.target;
      if (code) {
        const text = code.innerText;
        tools.copyToClipboard(text);
      }
    }
  });
};

const onMarkdownClick = (e) => {
  console.log(`markdownSourceText:\n\n${props.markdownText}`);
};

onMounted(() => {
  renderDownlatex();
  // initCopyEvent();
});

watch(
  () => {
    return props.markdownText;
  },
  () => {
    renderDownlatex();
  }
);

watch(
  () => {
    return props.isWriting;
  },
  () => {
    renderDownlatex();
  }
);

defineExpose({});
</script>

<style>
/* 闪烁动画 */
@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}
.katex-html {
  display: none;
}

.markdown-viewer {
  width: 100%;
  font-family: HarmonyOS Sans SC;
  font-size: 12px;
  font-weight: normal;
  line-height: 20px;
  color: #ffffff;
  strong {
    font-weight: 600;
    font-size: 14px;
  }
  h3 {
    margin: 10px 0 2px 0;
  }
  td {
    border: 1px solid #dfe2e5;
    padding: 5px;
  }
  th {
    border: 1px solid #dfe2e5;
    padding: 5px;
    text-align: center;
  }
  tr:nth-child(2n-1) {
    background-color: #fafbfc;
  }
  .katex .katex-mathml {
    border: 0;
    height: 1px;
    overflow: unset;
    padding: 0;
    position: unset;
  }
  .cursor-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    background-color: #fff;
    border-radius: 50%;
    animation: blink 1s infinite;
    vertical-align: middle;
    margin-left: 6px;
  }
  pre {
    padding: 12px;
    background-color: rgba(2, 86, 255, 0.05);
    border-radius: 12px;
    margin: 8px 0;
    line-height: 26px;
    position: relative;
    cursor: copy;

    code {
      max-width: calc(100% - 30px);
      width: 100%;
      overflow-wrap: break-word;
      word-wrap: break-word;
      white-space: pre-wrap;
      display: block;
    }
    /*::before {
      content: "  ";
      display: block;
      position: absolute;
      right: 12px;
      top: 12px;
      width: 20px;
      height: 20px;
      background-image: url("@/assets/img/copy-ico.png");
      background-size: 100% 100%;
      opacity: 0.5;
    }
    :hover {
      background-color: rgba(2, 86, 255, 0.1);
      &::before {
        opacity: 1;
      }
    }*/
  }
}
</style>
